import React, { useEffect, useState } from 'react';
import api from '@/lib/api';

const STATUS_LABEL = {
  NOT_CONFIGURED:          { icon: '⚪', text: '미등록',       tone: 'text-neutral-500' },
  CREDENTIAL_REQUIRED:     { icon: '🟡', text: '인증정보 대기', tone: 'text-amber-600' },
  SECRET_STORAGE_REQUIRED: { icon: '🟡', text: 'Secret 저장소 필요', tone: 'text-amber-600' },
  CONNECTED:               { icon: '🟢', text: '연결됨',       tone: 'text-emerald-600' },
  AUTH_FAILED:             { icon: '🔴', text: '인증 실패',     tone: 'text-rose-600' },
  INVALID_CONTRACT:        { icon: '🔴', text: '계약 오류',     tone: 'text-rose-600' },
  NETWORK_ERROR:           { icon: '🔴', text: '통신 오류',     tone: 'text-rose-600' },
  CARRIER_ERROR:           { icon: '🔴', text: '택배사 오류',   tone: 'text-rose-600' },
};

/**
 * Reusable carrier accounts management panel.
 *
 * Props:
 *   scope: "seller" | "operator" — chooses base URL.
 *   testidPrefix: string — used to derive data-testids.
 */
export default function CarrierAccountsPanel({ scope = 'seller', testidPrefix = 'seller' }) {
  const base = scope === 'operator' ? '/api/operator/carrier-accounts' : '/api/seller/carrier-accounts';
  const [meta, setMeta] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [statusMap, setStatusMap] = useState({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  // add-form state
  const [showAdd, setShowAdd] = useState(false);
  const [carrierCode, setCarrierCode] = useState('cj');
  const [contract, setContract] = useState({});
  const [creds, setCreds] = useState({});
  const [label, setLabel] = useState('');
  const [isDefault, setIsDefault] = useState(false);

  const refresh = async () => {
    try {
      const [m, a] = await Promise.all([
        api.get('/api/logistics/carriers/meta'),
        api.get(base),
      ]);
      setMeta(m.data.items || []);
      setAccounts(a.data.items || []);
    } catch (e) {
      setMsg('목록을 불러오지 못했습니다.');
    }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [scope]);

  const currentMeta = meta.find(x => x.carrier_code === carrierCode) || {};

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setMsg('');
    try {
      const created = await api.post(base, {
        carrier_code: carrierCode,
        label: label || null,
        is_default: isDefault,
        contract_metadata: contract,
      });
      const acc = created.data;
      // If credential fields were filled, submit them separately (never in create)
      const filled = Object.fromEntries(
        Object.entries(creds).filter(([, v]) => v && String(v).trim())
      );
      if (Object.keys(filled).length > 0) {
        await api.put(`${base}/${acc.account_id}/credentials`, filled);
      }
      setShowAdd(false);
      setContract({}); setCreds({}); setLabel(''); setIsDefault(false);
      await refresh();
    } catch (err) {
      setMsg(err?.response?.data?.detail || '저장 실패');
    } finally { setBusy(false); }
  };

  const runTest = async (accountId) => {
    setStatusMap(s => ({ ...s, [accountId]: { status: 'LOADING' } }));
    try {
      const r = await api.post(`${base}/${accountId}/test`);
      setStatusMap(s => ({ ...s, [accountId]: r.data }));
    } catch (err) {
      setStatusMap(s => ({ ...s, [accountId]: { status: 'CARRIER_ERROR' } }));
    }
  };

  const deleteCreds = async (accountId) => {
    if (!window.confirm('저장된 인증정보를 삭제하시겠습니까? (계약은 유지됩니다)')) return;
    try {
      await api.delete(`${base}/${accountId}/credentials`);
      await refresh();
    } catch { setMsg('삭제 실패'); }
  };

  const toggleActive = async (acc) => {
    await api.patch(`${base}/${acc.account_id}`, { is_active: !acc.is_active });
    await refresh();
  };

  const setDefault = async (acc) => {
    await api.patch(`${base}/${acc.account_id}`, { is_default: true });
    await refresh();
  };

  return (
    <div className="max-w-4xl" data-testid="carrier-accounts-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold">택배사 계약 관리</h2>
          <p className="text-sm text-neutral-500 mt-1">
            자신의 택배 계약을 등록해두면 향후 자동 송장발급 기능과 연결됩니다.
            현재 단계에서는 계약정보 저장 및 인증정보 관리까지만 지원합니다.
          </p>
        </div>
        <button
          className="px-3 py-2 bg-neutral-900 text-white text-sm rounded"
          onClick={() => setShowAdd(v => !v)}
          data-testid="carrier-add-btn"
        >
          {showAdd ? '취소' : '+ 택배사 연결'}
        </button>
      </div>

      {msg && <div className="text-sm text-rose-600 mb-3" data-testid="carrier-msg">{msg}</div>}

      {showAdd && (
        <form onSubmit={submit} className="border p-4 rounded mb-6 space-y-3 bg-neutral-50"
              data-testid="carrier-add-form">
          <div>
            <label className="block text-xs mb-1">택배사</label>
            <select
              value={carrierCode}
              onChange={(e) => { setCarrierCode(e.target.value); setContract({}); setCreds({}); }}
              className="border rounded px-2 py-1 w-full text-sm"
              data-testid="carrier-select"
            >
              {meta.map(m => (
                <option key={m.carrier_code} value={m.carrier_code}>{m.carrier_name}</option>
              ))}
            </select>
            {currentMeta.api_status && (
              <div className="text-xs text-neutral-500 mt-1">
                상태: {currentMeta.api_status} · {currentMeta.waiting_for}
              </div>
            )}
          </div>

          {(currentMeta.required_contract_fields || []).length > 0 && (
            <div>
              <div className="text-xs mb-2">계약 식별정보 (공개값)</div>
              {(currentMeta.required_contract_fields || []).map(f => (
                <div key={f} className="mb-2">
                  <label className="block text-xs text-neutral-500">{f}</label>
                  <input
                    type="text"
                    className="border rounded px-2 py-1 w-full text-sm"
                    value={contract[f] || ''}
                    onChange={e => setContract({ ...contract, [f]: e.target.value })}
                    data-testid={`carrier-contract-${f}`}
                  />
                </div>
              ))}
            </div>
          )}

          {(currentMeta.required_credential_fields || []).length > 0 && (
            <div>
              <div className="text-xs mb-2">인증정보 (Secret · 저장 시 암호화됨)</div>
              {(currentMeta.required_credential_fields || []).map(f => (
                <div key={f} className="mb-2">
                  <label className="block text-xs text-neutral-500">{f}</label>
                  <input
                    type="password"
                    autoComplete="new-password"
                    className="border rounded px-2 py-1 w-full text-sm"
                    value={creds[f] || ''}
                    onChange={e => setCreds({ ...creds, [f]: e.target.value })}
                    data-testid={`carrier-cred-${f}`}
                  />
                </div>
              ))}
            </div>
          )}

          <div>
            <label className="block text-xs mb-1">별칭 (선택)</label>
            <input
              type="text"
              className="border rounded px-2 py-1 w-full text-sm"
              value={label} onChange={e => setLabel(e.target.value)}
              placeholder="예: 메인 계약"
              data-testid="carrier-label"
            />
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={isDefault}
                    onChange={e => setIsDefault(e.target.checked)}
                    data-testid="carrier-is-default" />
            기본 택배사로 사용
          </label>

          <button
            disabled={busy}
            className="px-4 py-2 bg-emerald-600 text-white text-sm rounded disabled:opacity-50"
            data-testid="carrier-save"
          >
            저장
          </button>
        </form>
      )}

      <div className="divide-y border rounded" data-testid="carrier-accounts-list">
        {accounts.length === 0 && (
          <div className="p-6 text-sm text-neutral-500 text-center">
            아직 등록된 택배사 계약이 없습니다.
          </div>
        )}
        {accounts.map(acc => {
          const st = statusMap[acc.account_id];
          const stMeta = st && (STATUS_LABEL[st.status] || STATUS_LABEL.CARRIER_ERROR);
          return (
            <div key={acc.account_id} className="p-4 flex items-center justify-between"
                  data-testid={`carrier-row-${acc.account_id}`}>
              <div>
                <div className="font-medium">
                  {acc.carrier_name || acc.carrier_code}
                  {acc.is_default && (
                    <span className="ml-2 text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded">
                      기본
                    </span>
                  )}
                  {!acc.is_active && (
                    <span className="ml-2 text-xs bg-neutral-200 text-neutral-600 px-2 py-0.5 rounded">
                      비활성
                    </span>
                  )}
                </div>
                <div className="text-xs text-neutral-500 mt-1">
                  {acc.label || '별칭 없음'} ·
                  {' '}
                  {acc.has_credential
                    ? `인증정보 등록됨 (${(acc.credential_fields_configured || []).join(', ') || '기본'})`
                    : '인증정보 미등록'}
                </div>
                {stMeta && (
                  <div className={`text-xs mt-1 ${stMeta.tone}`}
                        data-testid={`carrier-status-${acc.account_id}`}>
                    {stMeta.icon} {stMeta.text}
                    {st?.reason ? ` · ${st.reason}` : ''}
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-1 items-end">
                <button className="text-xs px-2 py-1 border rounded"
                         onClick={() => runTest(acc.account_id)}
                         data-testid={`carrier-test-${acc.account_id}`}>
                  연결 테스트
                </button>
                {!acc.is_default && (
                  <button className="text-xs px-2 py-1 border rounded"
                           onClick={() => setDefault(acc)}
                           data-testid={`carrier-set-default-${acc.account_id}`}>
                    기본으로 설정
                  </button>
                )}
                <button className="text-xs px-2 py-1 border rounded"
                         onClick={() => toggleActive(acc)}
                         data-testid={`carrier-toggle-${acc.account_id}`}>
                  {acc.is_active ? '비활성화' : '활성화'}
                </button>
                {acc.has_credential && (
                  <button className="text-xs px-2 py-1 border rounded text-rose-600"
                           onClick={() => deleteCreds(acc.account_id)}
                           data-testid={`carrier-cred-delete-${acc.account_id}`}>
                    인증정보 삭제
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
